// Analytics tracking utility for Reel Maker
class AnalyticsTracker {
  constructor() {
    this.isInitialized = false;
    this.userSession = {
      startTime: Date.now(),
      uploadCount: 0,
      processingCount: 0,
      successCount: 0,
      errorCount: 0
    };
  }

  // Initialize analytics
  init() {
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
      this.trackPageView();
      this.trackSessionStart();
    }
  }

  // Track page view
  trackPageView(pageName = 'home') {
    if (!this.isInitialized) return;
    
    window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID || 'G-LFJPL5FWQR', {
      page_title: `Reel Maker - ${pageName}`,
      page_location: window.location.href,
      custom_map: {
        'dimension1': 'user_type',
        'dimension2': 'processing_method',
        'dimension3': 'video_duration',
        'dimension4': 'upload_count'
      }
    });
  }

  // Track session start
  trackSessionStart() {
    if (!this.isInitialized) return;
    
    window.trackEvent('session_start', {
      label: 'user_session',
      value: 1,
      session_id: this.generateSessionId(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    });
  }

  // Track file uploads
  trackFileUpload(fileType, fileCount, totalSize) {
    if (!this.isInitialized) return;
    
    this.userSession.uploadCount += fileCount;
    
    window.trackEvent('file_upload', {
      label: fileType,
      value: fileCount,
      file_count: fileCount,
      total_size_mb: Math.round(totalSize / (1024 * 1024) * 100) / 100,
      upload_count_total: this.userSession.uploadCount
    });
  }

  // Track processing start
  trackProcessingStart(method, imageCount, audioDuration) {
    if (!this.isInitialized) return;
    
    this.userSession.processingCount++;
    
    window.trackEvent('processing_start', {
      label: method,
      value: 1,
      processing_method: method,
      image_count: imageCount,
      audio_duration: audioDuration,
      processing_count: this.userSession.processingCount
    });
  }

  // Track processing progress
  trackProcessingProgress(progress, stage) {
    if (!this.isInitialized) return;
    
    window.trackEvent('processing_progress', {
      label: stage,
      value: progress,
      progress_percentage: progress,
      processing_stage: stage
    });
  }

  // Track processing completion
  trackProcessingComplete(method, processingTime, videoSize, success = true) {
    if (!this.isInitialized) return;
    
    if (success) {
      this.userSession.successCount++;
    } else {
      this.userSession.errorCount++;
    }
    
    window.trackEvent('processing_complete', {
      label: success ? 'success' : 'error',
      value: processingTime,
      processing_method: method,
      processing_time_seconds: processingTime,
      video_size_kb: Math.round(videoSize / 1024),
      success_count: this.userSession.successCount,
      error_count: this.userSession.errorCount
    });
  }

  // Track video download
  trackVideoDownload(videoSize, processingTime) {
    if (!this.isInitialized) return;
    
    window.trackEvent('video_download', {
      label: 'download_completed',
      value: videoSize,
      video_size_kb: Math.round(videoSize / 1024),
      processing_time_seconds: processingTime
    });
  }

  // Track user interactions
  trackUserInteraction(action, details = {}) {
    if (!this.isInitialized) return;
    
    window.trackUserAction(action, {
      ...details,
      timestamp: Date.now(),
      session_duration: Date.now() - this.userSession.startTime
    });
  }

  // Track performance metrics
  trackPerformance(metric, value) {
    if (!this.isInitialized) return;
    
    window.trackPerformance(metric, value);
  }

  // Track errors
  trackError(errorType, errorMessage, context = {}) {
    if (!this.isInitialized) return;
    
    window.trackEvent('error', {
      label: errorType,
      value: 1,
      error_message: errorMessage,
      error_context: context,
      timestamp: Date.now()
    });
  }

  // Track GPU acceleration usage
  trackGPUUsage(enabled, success) {
    if (!this.isInitialized) return;
    
    window.trackEvent('gpu_usage', {
      label: enabled ? 'enabled' : 'disabled',
      value: success ? 1 : 0,
      gpu_enabled: enabled,
      gpu_success: success
    });
  }

  // Track beat detection
  trackBeatDetection(beatCount, audioDuration, method) {
    if (!this.isInitialized) return;
    
    window.trackEvent('beat_detection', {
      label: method,
      value: beatCount,
      beat_count: beatCount,
      audio_duration: audioDuration,
      detection_method: method
    });
  }

  // Track session end
  trackSessionEnd() {
    if (!this.isInitialized) return;
    
    const sessionDuration = Date.now() - this.userSession.startTime;
    
    window.trackEvent('session_end', {
      label: 'session_complete',
      value: sessionDuration / 1000,
      session_duration_seconds: sessionDuration / 1000,
      upload_count: this.userSession.uploadCount,
      processing_count: this.userSession.processingCount,
      success_count: this.userSession.successCount,
      error_count: this.userSession.errorCount
    });
  }

  // Generate session ID
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Track custom dimensions
  setCustomDimension(dimension, value) {
    if (!this.isInitialized) return;
    
    window.gtag('config', 'G-LFJPL5FWQR', {
      [dimension]: value
    });
  }

  // Track custom metrics
  setCustomMetric(metric, value) {
    if (!this.isInitialized) return;
    
    window.gtag('event', 'custom_metric', {
      event_category: 'custom_metrics',
      event_label: metric,
      value: value
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

// Initialize on load
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    analytics.init();
  });
  
  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    analytics.trackSessionEnd();
  });
}

export default analytics; 